import React, { useState } from 'react';
import { useEntityList, createEntity, updateEntity, deleteEntity } from '../data/entities';
import { useAuth } from '../AuthContext';

// Listen datasets: shared-EFS directories visible to workloads and pipeline
// blocks. All file ops go through request fields on the dataset doc — the
// backend dataset worker performs them and writes listings back (no REST).
export default function WorkspaceDatasets({ orgId, workspaceId, compact = false }) {
  const { user } = useAuth();
  const scope = { org: orgId, workspace: workspaceId };
  const { docs: datasets } = useEntityList('dataset', scope);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);

  return (
    <section style={{ marginTop: compact ? 12 : 28 }}>
      {!compact && (
        <>
          <h2 style={{ margin: 0 }}>Listen · Datasets</h2>
          <p className="cs-help" style={{ marginTop: 4 }}>
            Files on shared storage — mounted into every workload and pipeline
            block. Use the <code>input.dataset</code> / <code>output.dataset</code>{' '}
            blocks or <code>ORG_DATASETS_DIR</code> in workload scripts.
          </p>
        </>
      )}

      <form
        style={{ display: 'flex', gap: 8, marginTop: 12, maxWidth: 480 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim() || busy) return;
          setBusy(true);
          setError('');
          try {
            await createEntity('dataset', scope, { name: name.trim(), createdBy: user.uid });
            setName('');
          } catch (err) {
            setError(err.message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <input
          className="cs-input"
          placeholder="New dataset name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">
          Create
        </button>
      </form>
      {error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 8 }}>{error}</p>}

      {datasets && !datasets.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>No datasets yet.</div>
      )}
      <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        {(datasets || []).map((d) => (
          <DatasetCard
            key={d.id}
            d={d}
            scope={scope}
            open={openId === d.id}
            onToggle={() => setOpenId(openId === d.id ? null : d.id)}
          />
        ))}
      </div>
    </section>
  );
}

function DatasetCard({ d, scope, open, onToggle }) {
  const [importName, setImportName] = useState('');
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);
  const files = d.files || [];
  const stats = d.stats || {};

  return (
    <div className="cs-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong>{d.name}</strong>
        <span className="cs-help" style={{ fontSize: 12 }}>
          {d.status === 'ready'
            ? `${stats.fileCount ?? files.length} file${(stats.fileCount ?? files.length) === 1 ? '' : 's'} · ${formatBytes(stats.totalBytes || 0)}`
            : d.status}
        </span>
        <code className="cs-help" style={{ fontSize: 11 }}>{d.id}</code>
        <span style={{ flex: 1 }} />
        <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={onToggle}>
          {open ? 'Close' : 'Browse'}
        </button>
        <button
          className="cs-btn cs-btn-outline cs-btn-sm"
          onClick={async () => {
            // eslint-disable-next-line no-alert
            if (window.confirm(`Delete dataset "${d.name}"? Files on storage are kept.`)) {
              await deleteEntity('dataset', scope, d.id);
            }
          }}
        >
          Delete
        </button>
      </div>
      {d.error && <p className="cs-help" style={{ color: 'var(--danger, #e5484d)', marginTop: 6 }}>{d.error}</p>}

      {open && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="cs-btn cs-btn-outline cs-btn-sm"
              onClick={() => updateEntity('dataset', scope, d.id, { refreshRequested: true })}
            >
              Refresh listing
            </button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setShowImport((s) => !s)}>
              {showImport ? 'Cancel import' : 'Import text file'}
            </button>
          </div>

          {showImport && (
            <form
              style={{ display: 'grid', gap: 8, marginTop: 10 }}
              onSubmit={async (e) => {
                e.preventDefault();
                if (!importName.trim()) return;
                await updateEntity('dataset', scope, d.id, {
                  importRequest: { name: importName.trim(), content: importText },
                });
                setImportName('');
                setImportText('');
                setShowImport(false);
              }}
            >
              <input
                className="cs-input"
                placeholder="filename.txt"
                value={importName}
                onChange={(e) => setImportName(e.target.value)}
              />
              <textarea
                className="cs-input"
                style={{ minHeight: 100, fontFamily: 'monospace', fontSize: 13 }}
                placeholder="file contents…"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                spellCheck={false}
              />
              <div>
                <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!importName.trim()}>
                  Import
                </button>
              </div>
            </form>
          )}

          <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
            {!files.length && <span className="cs-help">No files.</span>}
            {files.map((f) => (
              <div key={f.path} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
                <button
                  className="cs-btn-link"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent, #4f8cff)', fontFamily: 'monospace' }}
                  onClick={() => updateEntity('dataset', scope, d.id, { previewRequest: { path: f.path } })}
                >
                  {f.path}
                </button>
                <span className="cs-help" style={{ fontSize: 12 }}>{formatBytes(f.size)}</span>
                <span className="cs-help" style={{ fontSize: 12 }}>{new Date(f.mtime).toLocaleString()}</span>
              </div>
            ))}
          </div>

          {d.preview?.path && (
            <div style={{ marginTop: 10 }}>
              <p className="cs-help" style={{ margin: '0 0 4px' }}>
                Preview: <code>{d.preview.path}</code>{d.preview.truncated ? ' (truncated)' : ''}
              </p>
              <pre
                className="cs-card"
                style={{ padding: 10, margin: 0, maxHeight: 260, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap' }}
              >
                {d.preview.content}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
